import { AskRequest, AskRequestComment } from '@hone-automation/common/lib/AskRequests';
import { Transaction } from '@hone-automation/common/lib/Transactions';

/**
 * Structure that separates requests into personal and team categories
 */
export type SplitedRequests = {
  /** Requests that belong to the current user */
  me: AskRequest[];
  /** Requests that belong to other team members */
  team: AskRequest[];
};

/**
 * Structure that separates requests into priority categories
 */
export type PrioritySplittedRequests = {
  Blocker: AskRequest[];
  Normal: AskRequest[];
};

/**
 * Filter criteria for request filtering and sorting
 */
export type FilterProps = {
  /** Status filter for displaying Open, In Progress, or Closed requests */
  status: 'Open' | 'In Progress' | 'Closed';
  /** Text to filter requests by their content, transaction details, or comments */
  search?: string;
  /** Sort order for requests based on timestamp */
  sort?: 'oldest' | 'newest';
  /** Grouping method for organizing requests in the UI */
  groupBy?: 'date' | 'location';
};

const TEXT_FIELDS_TO_SEARCH = {
  cell: ['identifier', 'locationId', 'comment'],
  transaction: ['id', 'externalId', 'name', 'account', 'memo', 'externalTransactionType', 'externalAccountId'],
  comment: ['comment'],
};
const NUMERIC_FIELDS_TO_SEARCH = {
  cell: ['flaggedAmount'],
  transaction: ['amount'],
};
const SEARCH_OPERATORS = {
  GREATER_THAN: '>',
  LESS_THAN: '<',
} as const;

/**
 * Searches through requests for text matching in various fields
 *
 * @param {AskRequest[]} requests - Array of request objects to search through
 * @param {string} search - Text string to search for
 * @returns {AskRequest[]} Array of requests that match the search criteria
 */
const searchByText = (requests: AskRequest[], search: string): AskRequest[] => {
  const matches: AskRequest[] = [];
  requests.forEach(request => {
    const foundTargets = [];
    //cell
    TEXT_FIELDS_TO_SEARCH.cell.forEach(field => {
      const requestField = request[field as keyof AskRequest] as string;
      if (requestField && requestField.toLowerCase().includes(search.toLowerCase())) {
        foundTargets.push(requestField);
      }
    });
    //transaction
    if (request.transaction) {
      TEXT_FIELDS_TO_SEARCH.transaction.forEach(field => {
        const transactionField = request.transaction![field as keyof Transaction] as string;
        if (transactionField && transactionField.toLowerCase().includes(search.toLowerCase())) {
          foundTargets.push('transaction.' + transactionField);
        }
      });
    }
    //comment
    if (request.comments && request.comments.length > 0) {
      request.comments.forEach((comment, index) => {
        TEXT_FIELDS_TO_SEARCH.comment.forEach(field => {
          const commentField = comment[field as keyof AskRequestComment] as string;
          if (commentField && commentField.toLowerCase().includes(search.toLowerCase())) {
            foundTargets.push('comment.' + index + '.' + field);
          }
        });
      });
    }
    if (foundTargets.length > 0) {
      matches.push(request);
    }
  });
  return matches;
};

/**
 * Searches through requests for amounts that match comparison criteria
 * Handles greater than (>) and less than (<) operators
 *
 * @param {AskRequest[]} requests - Array of request objects to search through
 * @param {string} search - Search string with format like '>100' or '<50'
 * @returns {AskRequest[]} Array of requests with amounts that match the comparison
 */
const searchByAmount = (requests: AskRequest[], search: string): AskRequest[] => {
  const matches: AskRequest[] = [];

  const searchValue = Number(search.substring(1));
  const sign = search.substring(0, 1);
  if (isNaN(searchValue)) {
    return [];
  }
  requests.forEach(request => {
    const foundTargets = [];
    //cell
    NUMERIC_FIELDS_TO_SEARCH.cell.forEach(field => {
      const requestField = request[field as keyof AskRequest] as number;
      if (requestField) {
        if (sign === SEARCH_OPERATORS.GREATER_THAN && requestField > searchValue) {
          foundTargets.push(requestField);
        } else if (sign === SEARCH_OPERATORS.LESS_THAN && requestField < searchValue) {
          foundTargets.push(requestField);
        }
      }
    });
    //transaction
    if (request.transaction) {
      NUMERIC_FIELDS_TO_SEARCH.transaction.forEach(field => {
        const transactionField = request.transaction![field as keyof Transaction] as number;
        if (transactionField) {
          if (sign === SEARCH_OPERATORS.GREATER_THAN && transactionField > searchValue) {
            foundTargets.push(transactionField);
          } else if (sign === SEARCH_OPERATORS.LESS_THAN && transactionField < searchValue) {
            foundTargets.push(transactionField);
          }
        }
      });
    }
    if (foundTargets.length > 0) {
      matches.push(request);
    }
  });
  return matches;
};

/**
 * Filters requests based on their status (Open, In Progress, Closed)
 *
 * @param {AskRequest[]} requests - Array of request objects to filter
 * @param {FilterProps['status']} status - Status to filter by
 * @returns {AskRequest[]} Array of requests matching the specified status
 */
export const filterRequestsByStatus = (requests: AskRequest[], status: FilterProps['status']): AskRequest[] => {
  if (!requests || requests.length === 0) {
    return [];
  }
  if (status === 'Closed') {
    return requests
      .filter(request => request.status === 'Closed')
      .sort((a, b) => {
        return new Date(a.updatedAt || a.createdAt).getTime() - new Date(b.updatedAt || b.createdAt).getTime();
      })
      .reverse();
  }
  return requests.filter(request => request.status === status);
};

/**
 * Filters requests based on a search string, which can include text or numeric comparisons
 *
 * @param {AskRequest[]} requests - Array of request objects to filter
 * @param {string} search - Search string to filter by
 * @returns {AskRequest[]} Array of requests matching the search criteria
 */
export const filterRequestsBySearch = (requests: AskRequest[], search: string): AskRequest[] => {
  if (!requests || requests.length === 0) {
    return [];
  }
  if (!search || search === '') {
    return requests;
  }
  if (search.indexOf(SEARCH_OPERATORS.GREATER_THAN) === 0 || search.indexOf(SEARCH_OPERATORS.LESS_THAN) === 0) {
    return searchByAmount(requests, search);
  } else {
    return searchByText(requests, search);
  }
};

/**
 * Divides requests into two categories: requests handled by the current user ('me')
 * and requests handled by other team members ('team')
 *
 * @param {AskRequest[]} requests - Array of request objects to categorize
 * @param {string} meId - ID of the current user
 * @returns {SplitedRequests|null} Object with 'me' and 'team' arrays, or null if no requests
 */
const filterMeAndTeamRequests = (requests: AskRequest[], meId: string): SplitedRequests | null => {
  if (!requests || requests.length === 0) {
    return null;
  }
  const filteredRequests: SplitedRequests = { me: [], team: [] };
  requests.forEach((request: AskRequest) => {
    if (request.comments && request.comments.length > 0) {
      const lastComment = request.comments[request.comments.length - 1];
      if (lastComment.userLocation?.email === meId || lastComment.userLocation?.userId === meId) {
        filteredRequests.me.push(request);
      } else {
        filteredRequests.team.push(request);
      }
    }
  });
  return filteredRequests;
};

/**
 * Splits an array of AskRequest into groups by priority ('Blocker' and 'Normal').
 * @param {AskRequest[]} requests - Array of requests to split
 * @returns {PrioritySplittedRequests} Object with Blocker and Normal arrays
 */
export function splitRequestsByPriority(requests: AskRequest[]): PrioritySplittedRequests {
  if (!Array.isArray(requests)) {
    return { Blocker: [], Normal: [] };
  }
  return requests.reduce<PrioritySplittedRequests>(
    (acc, req) => {
      // @ts-ignore
      if (req.priority === 'Blocker') {
        acc.Blocker.push(req);
      } else {
        acc.Normal.push(req);
      }
      return acc;
    },
    { Blocker: [], Normal: [] }
  );
}

/**
 * Main filtering function that applies all filters and splits requests between 'me' and 'team'
 *
 * @param {AskRequest[]} requests - Array of request objects to filter and split
 * @param {string} meId - ID of the current user
 * @param {FilterProps} filter - Object containing all filter criteria
 * @returns {SplitedRequests|null} Object with filtered 'me' and 'team' arrays, or null if invalid input
 */
export const filterAndSplitRequests = (
  requests: AskRequest[],
  meId: string,
  filter: FilterProps
): SplitedRequests | null => {
  if (!requests || !requests.length || !meId) return null;
  const filteredRequests = filterRequests(requests, filter);
  return filterMeAndTeamRequests(filteredRequests, meId);
};

export const filterRequests = (requests: AskRequest[], filter: FilterProps): AskRequest[] => {
  const { status, search } = filter;
  const filteredRequests = filterRequestsByStatus(requests, status);
  return filterRequestsBySearch(filteredRequests, search || '');
};
